import React, {useState} from "react";
import {ExclamationCircleIcon, CalendarIcon, XMarkIcon} from "@heroicons/react/24/solid";
import Label from "./Label";
import {useIntl} from "react-intl";

function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const validator = (type, value) => {
  switch (type) {
    case "email":
      return validateEmail(value);
    case "time":
      return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

    default:
      return true;
  }
};

const Input = ({
                 value,
                 label,
                 name,
                 type = "text",
                 validation,
                 required,
                 errorMessage,
                 showCalendar,
                 showErrorDialog = true,
                 numberOptions = {
                   minimumFractionDigits: 0,
                   maximumFractionDigits: 2,
                 },
                 materialTable = false,
                 onClear,
                 ...rest
               }) => {
  const [valid, setValid] = useState(true);
  const [focus, setFocus] = useState(false);
  const intl = useIntl();
  const containerClassMargin = !showErrorDialog || showCalendar || !valid ? 'mb-0' : 'mb-4';
  const containerClassAlignment =
    type === "hidden" ? "hidden" :
    `${containerClassMargin} flex shadow-sm items-center relative`;
  const containerClassBorder =
    type === "hidden" ? "mt-1" :
      materialTable ? "border pl-2.5 pr-1 border-am-600 rounded-md bg-table-100" :
        "mt-1 border rounded-md border-gray-300 focus:border-gray-900 hover:border-gray-900";

  let inputValue = type === 'number' ? value : value || "";

  if (inputValue && type === "number" && !focus) {
    inputValue = intl.formatNumber(inputValue, numberOptions);
  }

  const defaultProps = {
    onFocus: () => {
      if (type === "number") {
        setFocus(true);
      }
      setValid(true);
    },
    onBlur: (e) => {
      if (type === "number") {
        setFocus(false);
      }
      if (required) {
        if (!e.target.value) {
          setValid(false);
          return;
        }

        if (validation) {
          setValid(validation(e.target.value));
          return;
        }

        setValid(validator(type, e.target.value));
      }
    },
    type: type === "number" ? (focus ? type : "text") : type,
    name: type,
    className: `px-2 py-2 border-transparent focus:ring-transparent focus:border-transparent block w-full sm:text-sm disabled:opacity-50 ${materialTable ? "bg-table-100 h-8" : "rounded-md"}`,
    value: inputValue,
    readOnly: type === "number" && !focus,
    ...rest,
  };

  return (
    <>
      {label ? <Label>{label}</Label> : null}
      <div
        className={`${containerClassAlignment} ${containerClassBorder} relative`}>
        {typeof showCalendar !== "undefined" ? (
          <CalendarIcon
            className="h-6 w-6 text-gray-400 ml-1"
            aria-hidden="true"
          />
        ) : null}
        {type === "textarea" ? (
          <textarea {...defaultProps}/>
        ) : (
          <>
            <input {...defaultProps}/>
            {inputValue && onClear ?
              <XMarkIcon
                className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-700"
                aria-hidden="true"
                onClick={onClear}
              /> : null
            }
          </>
        )}
        {!valid ? (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        ) : null}
      </div>
      {!valid && errorMessage && (
        <p className="mt-1 text-xs text-red-600" id="email-error">
          {errorMessage}
        </p>
      )}
    </>
  );
};

export default Input;
